/**
 * push 域 Zod 共享契约 — ADM-PUSH-S001~S005
 *
 * #104 tombstone：push_templates 归 C 端 own（canonical key = template_slug，category 闭集，无 deep_link）。
 * push_tasks 仍 admin-own（方案 A：保留 task_type 历史语义 + 加 template_slug 关联；createTask 按 template_slug 反查模板）。
 */

import { z } from 'zod';

const LocaleSchema = z.string().min(2).max(16);

/**
 * push_template 文案 i18n（#104 / codex #22）：**en 必填且非空白**（C 端渲染兜底 + DB CHECK 用 btrim 拒空白 en），
 * zh / 其他 locale 可选（zh=admin preview，不进 C 端渲染集合）。区别于 faq 的 zh+en 双必填——push 的 zh 仅预览。
 * 注：service 对既有行做 **merge 保留**（不 drop 未提交 locale，如 canonical 的 pl），避免抹掉 C 端本地化数据。
 */
/**
 * 模板文案长度上限（dev-docs `02-API契约/A4-运营推送.md` §2.2）：按模板原文的 Unicode 码点计，
 * 占位符 `{var}` 按字面计入、不展开。人工保存与自动补齐（`86-运营配置自动翻译.md` §1.7）共用。
 */
export const PUSH_TEMPLATE_TITLE_MAX_CODE_POINTS = 120;
export const PUSH_TEMPLATE_BODY_MAX_CODE_POINTS = 2000;

/** 按 Unicode 码点计长度（emoji 等代理对按 1 个计），与契约口径一致 */
export function pushTemplateCodePointLength(text: string): number {
  return Array.from(text).length;
}

function pushTemplateI18nSchema(maxCodePoints: number) {
  return z
    .record(
      LocaleSchema,
      // 非 en 语言允许传空串：把该语言清空即让自动补齐重新译出（`86-运营配置自动翻译.md` §1.7）
      z
        .string()
        .refine((value) => pushTemplateCodePointLength(value) <= maxCodePoints, {
          message: `must be at most ${maxCodePoints} Unicode code points`,
        }),
    )
    .refine((m) => typeof m.en === 'string' && m.en.trim().length > 0, {
      message: 'en is required and must be non-blank',
    });
}

export const PushTaskTypeSchema = z.enum([
  'SIGNIN_REMIND',
  'USER_RECALL',
  'BOOK_RECOMMEND',
  'TOPUP_PROMO',
  'CHAPTER_UPDATE',
  'BOOK_DELIST_NOTICE',
  'SUBSCRIPTION_RENEWAL_REMIND',
  'SUBSCRIPTION_RENEWAL_SUCCESS',
  'SUPPORT_REPLY',
]);
export type PushTaskType = z.infer<typeof PushTaskTypeSchema>;

/** ADM-PUSH-S003：仅 BOOK_RECOMMEND / TOPUP_PROMO 允许运营主动创建 */
export const MANUAL_PUSH_TYPES: PushTaskType[] = ['BOOK_RECOMMEND', 'TOPUP_PROMO'];

/** push_templates.category 闭集（= push_messages.category，dev-docs §2 / #104）*/
export const PUSH_TEMPLATE_CATEGORIES = [
  'OFFER',
  'NOVEL_UPDATE',
  'NOVEL_DELISTED',
  'FEEDBACK',
  'TASK',
  'SUBSCRIPTION',
] as const;
export type PushTemplateCategory = (typeof PUSH_TEMPLATE_CATEGORIES)[number];

/** template_slug lower_snake_case（对齐 canonical CHECK `^[a-z0-9][a-z0-9_]*$`）*/
const PushTemplateSlugSchema = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[a-z0-9][a-z0-9_]*$/, 'lower_snake_case: lowercase letters / digits / underscore');

/**
 * §2.5.2 注册表镜像：slug → category / functional / placeholders。
 * 功能类（isFunctional，category≠OFFER）server 自动触发、不可停；营销类（OFFER）admin 可停。
 *
 * **不写死条数**（ADM-PUSH-S001）：全集与计数以 `03-数据字典/100-推送 token.md` §2.5.2 注册表为准，
 * 本仓与 admin UI 都按本对象的实际行数渲染；注释里复述一个数字，上游加 slug 时它会静默变假。
 * 行序照抄该注册表，便于逐行对账。
 *
 * placeholders 对齐 `02-API契约/90-推送与通知.md` §5.2「占位符」列，必须与 runtime producer params 同源；
 * admin 编辑器只展示该列表，写入 schema / service 拒绝列表外变量（A4 §2.2 / §2.4）。
 * **两侧必须同时守**：只从 §5.2 摘掉一个变量而 admin 仍放行运营写回 `body_i18n`，按 §5.1「missing param
 * 保留 `{var}` 字面」用户收到的是字面 `{bonusAmount}` —— 比渲染出「0 bonus」更糟。
 *
 * contentSource 对齐 §2.5.2「内容来源注册」：`CONFIRMED_MESSAGE` 的模板行只登记分类与通知中心策略，
 * 实际文案来自单用户确认快照（A4 §9），模板页只读、`PUT /push/templates` 拒改（A4 §2.2）。
 */
export const PUSH_TEMPLATE_CONTENT_SOURCES = ['TEMPLATE', 'CONFIRMED_MESSAGE'] as const;
export type PushTemplateContentSource = (typeof PUSH_TEMPLATE_CONTENT_SOURCES)[number];

export type PushTemplateRegistryEntry = {
  category: PushTemplateCategory;
  isFunctional: boolean;
  placeholders: readonly string[];
  contentSource: PushTemplateContentSource;
};

export const PUSH_TEMPLATE_REGISTRY: Record<string, PushTemplateRegistryEntry> = {
  subscription_started: {
    category: 'SUBSCRIPTION',
    isFunctional: true,
    // 本变体是本期实发 BONUS 为 0 那一支（§5.2「订阅文案变体选取」），不带 `{bonusAmount}`
    placeholders: ['productName', 'coinAmount'],
    contentSource: 'TEMPLATE',
  },
  subscription_started_with_bonus: {
    category: 'SUBSCRIPTION',
    isFunctional: true,
    // 同一业务事件（订阅开通）的另一文案变体（本期实发 BONUS 大于 0）：category / 通知中心策略 /
    // payload 闭集 / deep link 全同，投递幂等键也不随变体分流；server 自动触发，无对应 admin task_type
    placeholders: ['productName', 'coinAmount', 'bonusAmount'],
    contentSource: 'TEMPLATE',
  },
  payment_settled: {
    category: 'SUBSCRIPTION',
    isFunctional: true,
    // 本变体是 `payment_settlements.bonus_amount = 0` 那一支，不带 `{bonusAmount}`
    placeholders: ['coinAmount'],
    contentSource: 'TEMPLATE',
  },
  payment_settled_with_bonus: {
    category: 'SUBSCRIPTION',
    isFunctional: true,
    // 同一业务事件的另一文案变体（`payment_settlements.bonus_amount > 0`）：category / 通知中心策略 /
    // payload 闭集 / deep link 全同，投递幂等键也不随变体分流；各占一行只因文案按 slug 存取
    placeholders: ['coinAmount', 'bonusAmount'],
    contentSource: 'TEMPLATE',
  },
  reading_pass_card_settled: {
    category: 'SUBSCRIPTION',
    isFunctional: true,
    // 该效果不发币、不动钱包，文案既不提金额也不写时长 → §5.2 判「—（无占位）」
    placeholders: [],
    contentSource: 'TEMPLATE',
  },
  subscription_renewed: {
    category: 'SUBSCRIPTION',
    isFunctional: true,
    // 本变体是本期实发 BONUS 为 0 那一支（§5.2「订阅文案变体选取」）
    placeholders: ['productName', 'coinAmount'],
    contentSource: 'TEMPLATE',
  },
  subscription_renewed_with_bonus: {
    category: 'SUBSCRIPTION',
    isFunctional: true,
    // 续费成功的实发 BONUS 大于 0 文案变体；与 subscription_renewed 共用幂等键，
    // server 自动触发、不经 push_tasks（task_type 仍只映射 subscription_renewed）
    placeholders: ['productName', 'coinAmount', 'bonusAmount'],
    contentSource: 'TEMPLATE',
  },
  chapter_update: {
    category: 'NOVEL_UPDATE',
    isFunctional: true,
    placeholders: ['novelTitle'],
    contentSource: 'TEMPLATE',
  },
  novel_delisted: {
    category: 'NOVEL_DELISTED',
    isFunctional: true,
    placeholders: ['novelTitle', 'reason'],
    contentSource: 'TEMPLATE',
  },
  feedback_reply: {
    category: 'FEEDBACK',
    isFunctional: true,
    placeholders: [],
    contentSource: 'TEMPLATE',
  },
  admin_user_notice: {
    category: 'FEEDBACK',
    isFunctional: true,
    // 单用户确认发送（ADM-PUSH-S007）：模板行 seed 的是只读说明，不是发送文案（§2.5.2 内容来源注册）
    placeholders: [],
    contentSource: 'CONFIRMED_MESSAGE',
  },
  signin_remind: {
    category: 'OFFER',
    isFunctional: false,
    placeholders: [],
    contentSource: 'TEMPLATE',
  },
  user_recall: {
    category: 'OFFER',
    isFunctional: false,
    placeholders: [],
    contentSource: 'TEMPLATE',
  },
  novel_recommend: {
    category: 'OFFER',
    isFunctional: false,
    placeholders: ['novelTitle'],
    contentSource: 'TEMPLATE',
  },
  topup_promo: {
    category: 'OFFER',
    isFunctional: false,
    placeholders: [],
    contentSource: 'TEMPLATE',
  },
  // 同一免费体验卡提醒的两个状态文案（未领取 / 已领取未激活），server cron 按当时状态二选一（90 §5.4 注 4）
  reading_pass_free_trial_claim_remind: {
    category: 'OFFER',
    isFunctional: false,
    placeholders: [],
    contentSource: 'TEMPLATE',
  },
  reading_pass_free_trial_activate_remind: {
    category: 'OFFER',
    isFunctional: false,
    placeholders: [],
    contentSource: 'TEMPLATE',
  },
};

/**
 * 内容来源为 CONFIRMED_MESSAGE 的 slug（§2.5.2 注册表「确认快照来源」）：全局模板行只作分类与
 * 通知中心策略登记，文案只读、不经模板写接口，也不进自动补齐（`86-运营配置自动翻译.md` §1.3）。
 * 从注册表的 contentSource 派生，不另维护清单。
 */
export const PUSH_TEMPLATE_CONFIRMED_MESSAGE_SLUGS: readonly string[] = Object.entries(
  PUSH_TEMPLATE_REGISTRY,
)
  .filter(([, entry]) => entry.contentSource === 'CONFIRMED_MESSAGE')
  .map(([slug]) => slug);

/**
 * 同一业务事件 / 同一提醒的文案变体（§2.5.2 注记）：自动补齐按组原子写入，避免同一类通知
 * 一个变体已译、另一个变体仍回退英文（`86-运营配置自动翻译.md` §1.7）。未列出的 slug 自成一组。
 */
export const PUSH_TEMPLATE_VARIANT_GROUPS: readonly (readonly string[])[] = [
  ['subscription_started', 'subscription_started_with_bonus'],
  ['payment_settled', 'payment_settled_with_bonus'],
  ['subscription_renewed', 'subscription_renewed_with_bonus'],
  ['reading_pass_free_trial_claim_remind', 'reading_pass_free_trial_activate_remind'],
];

/**
 * 按出现顺序取文案中的占位符名（含重复，供多重集比较）；花括号不成对或名称非法时返回 null。
 */
export function extractPushTemplatePlaceholders(text: string): string[] | null {
  const names: string[] = [];
  let cursor = 0;
  while (cursor < text.length) {
    const open = text.indexOf('{', cursor);
    const close = text.indexOf('}', cursor);
    if (open < 0 && close < 0) break;
    if (close >= 0 && (open < 0 || close < open)) return null;
    const end = text.indexOf('}', open + 1);
    if (end < 0) return null;
    const name = text.slice(open + 1, end);
    if (!/^\w+$/.test(name)) return null;
    names.push(name);
    cursor = end + 1;
  }
  return names;
}

export type PushTemplatePlaceholderIssue = { field: string; message: string };

const PUSH_PLACEHOLDER_NAME_RE = /^\w+$/;

/** 返回模板文案中 runtime producer 不会提供的占位符；未知 slug 由既有 registry guard 单独处理。 */
export function findUnsupportedPushTemplatePlaceholders(input: {
  template_slug: string;
  title_i18n: Record<string, string>;
  body_i18n: Record<string, string>;
}): PushTemplatePlaceholderIssue[] {
  const registryEntry = PUSH_TEMPLATE_REGISTRY[input.template_slug];
  if (!registryEntry) return [];

  const allowed = new Set(registryEntry.placeholders);
  const issues: PushTemplatePlaceholderIssue[] = [];
  const seen = new Set<string>();

  for (const [field, i18n] of [
    ['title_i18n', input.title_i18n],
    ['body_i18n', input.body_i18n],
  ] as const) {
    for (const [locale, text] of Object.entries(i18n)) {
      let cursor = 0;
      while (cursor < text.length) {
        const open = text.indexOf('{', cursor);
        const close = text.indexOf('}', cursor);
        if (close >= 0 && (open < 0 || close < open)) {
          const issueKey = `${field}.${locale}:malformed:${close}`;
          if (!seen.has(issueKey)) {
            seen.add(issueKey);
            issues.push({
              field: `${field}.${locale}`,
              message: `malformed placeholder '}' for template_slug '${input.template_slug}'`,
            });
          }
          cursor = close + 1;
          continue;
        }
        if (open < 0) break;

        const end = text.indexOf('}', open + 1);
        const raw = end < 0 ? text.slice(open) : text.slice(open, end + 1);
        const placeholder = end < 0 ? '' : text.slice(open + 1, end);
        const malformed = end < 0 || !PUSH_PLACEHOLDER_NAME_RE.test(placeholder);
        if (!malformed && allowed.has(placeholder)) {
          cursor = end + 1;
          continue;
        }

        const issueKey = `${field}.${locale}:${malformed ? 'malformed' : 'unsupported'}:${raw}`;
        if (!seen.has(issueKey)) {
          seen.add(issueKey);
          issues.push({
            field: `${field}.${locale}`,
            message: malformed
              ? `malformed placeholder '${raw}' for template_slug '${input.template_slug}'`
              : `unsupported placeholder '${raw}' for template_slug '${input.template_slug}'`,
          });
        }
        if (end < 0) break;
        cursor = end + 1;
      }
    }
  }

  return issues;
}

/**
 * 模板测试发送可测集（dev-docs `02-API契约/A4-运营推送.md` §7.4）：注册表全集减去「投递侧求值需要真实业务实体」
 * 的 slug。闭集逐字照抄契约，不按 category 推导 —— 判据是 payload 必带字段与投递侧 gate，不是分类。
 */
export const PUSH_TEMPLATE_TEST_SENDABLE_SLUGS: readonly string[] = [
  'novel_delisted',
  'feedback_reply',
  'signin_remind',
  'user_recall',
  'novel_recommend',
  'topup_promo',
];

/**
 * A4 §7.4「不可测」清单，逐字照抄契约。与可测集互斥、并集 = 注册表全集（push.test.ts 对账）。
 */
export const PUSH_TEMPLATE_TEST_UNSENDABLE_SLUGS: readonly string[] = [
  'subscription_started',
  'subscription_started_with_bonus',
  'payment_settled',
  'payment_settled_with_bonus',
  'reading_pass_card_settled',
  'subscription_renewed',
  'subscription_renewed_with_bonus',
  'chapter_update',
  'reading_pass_free_trial_claim_remind',
  'reading_pass_free_trial_activate_remind',
  'admin_user_notice',
];

/** POST /push/templates/:template_slug/test-send 请求体（A4 §7.3）；params 的 key 白名单由 service 按 slug 校验 */
export const TestSendPushTemplateRequestSchema = z
  .object({
    target_app_id: z.string().uuid(),
    target_user_id: z.string().uuid(),
    // 契约只定 object(string→string)；key 白名单按 slug 在 service 校验，值不另设长度上限（A4 §7.3）
    params: z.record(z.string(), z.string()).optional(),
  })
  .strict();
export type TestSendPushTemplateRequest = z.infer<typeof TestSendPushTemplateRequestSchema>;

/** A4 §7.7：enqueued_at = events_outbox 行落库时刻，语义是「已入队」而非「已投递」 */
export type TestSendPushTemplateResponse = {
  template_slug: string;
  target_app_id: string;
  target_user_id: string;
  enqueued_at: string;
};

/**
 * task_type → template_slug 桥接（push_tasks 方案 A：createTask 按 task_type 映射到 slug 反查模板）。
 * `SUBSCRIPTION_RENEWAL_REMIND` 映射的 slug 已移出 §2.5.2 注册表（A4 §8）：该值不再产生新任务，
 * 仅作历史任务快照与 `GET /push/tasks` 筛选值，故 task_type 仍为 9 值、映射保留。
 */
export const TEMPLATE_SLUG_BY_TASK_TYPE: Record<PushTaskType, string> = {
  SIGNIN_REMIND: 'signin_remind',
  USER_RECALL: 'user_recall',
  BOOK_RECOMMEND: 'novel_recommend',
  TOPUP_PROMO: 'topup_promo',
  CHAPTER_UPDATE: 'chapter_update',
  BOOK_DELIST_NOTICE: 'novel_delisted',
  SUBSCRIPTION_RENEWAL_REMIND: 'subscription_renewal_remind',
  SUBSCRIPTION_RENEWAL_SUCCESS: 'subscription_renewed',
  SUPPORT_REPLY: 'feedback_reply',
};

/**
 * 编辑 push_template 文案 / 启停（#104：admin 仅维护 baseline 的 title/body i18n + 启停，不新建 slug）。
 * 按 template_slug 定位（∈ 注册表）；slug/category/is_seed_baseline 由 server 拥有不可改；功能类禁停由 service guard。
 */
export const UpsertPushTemplateRequestSchema = z
  .object({
    template_slug: PushTemplateSlugSchema,
    title_i18n: pushTemplateI18nSchema(PUSH_TEMPLATE_TITLE_MAX_CODE_POINTS),
    body_i18n: pushTemplateI18nSchema(PUSH_TEMPLATE_BODY_MAX_CODE_POINTS),
    /** 仅营销类（OFFER）可改启停；功能类省略/改 false 由 service 拒（功能类禁停）*/
    is_active: z.boolean().optional(),
  })
  .superRefine((input, ctx) => {
    for (const issue of findUnsupportedPushTemplatePlaceholders(input)) {
      ctx.addIssue({
        code: 'custom',
        path: issue.field.split('.'),
        message: issue.message,
      });
    }
  });
export type UpsertPushTemplateRequest = z.infer<typeof UpsertPushTemplateRequestSchema>;

/**
 * 主动推送 payload 按 task_type 分形（ADM-PUSH-S003 @ dev-docs 660d6ac · #228 决议 3）：
 * - BOOK_RECOMMEND（书籍推荐）→ { book_id }（uuid）
 * - TOPUP_PROMO（充值活动）→ { offer_id }（uuid · app_payment_offers.id，运营支付活动实例；
 *   入口携带 `?offer_id=`，**不再选支付 scenario / 不下发 `?scenario=`**——限时优惠 + 充值活动统一 offer 实例）。
 *   service 写侧按目标 App + 投递时刻校验 offer 可服务，失败抛 PUSH_OFFER_NOT_FOUND。payload 严格单 offer_id
 *   （不引入 app_id→offer_id 映射；运营主动推送一律 single_app，张培 #244 决议 1）。
 * `.strict()`：拒多余 key，让落库 payload 恰好是契约形状（service 直存 input.payload）。
 */
export const PushBookRecommendPayloadSchema = z.object({ book_id: z.string().uuid() }).strict();
export const PushTopupPromoPayloadSchema = z.object({ offer_id: z.string().uuid() }).strict();

/**
 * 主动推送请求 = 按 task_type 的 discriminated union（codex r1：单 record+refine 只运行时校验、类型不收窄，
 * 内部 caller / codegen 仍能拼旧形状）。union 让 `z.infer` 按 task_type **收窄 payload 类型** + route 报精确
 * field 级错误。9 类 task_type 全列：BOOK_RECOMMEND/TOPUP_PROMO 强类型 payload；其余 7 个非 manual 类型
 * payload 宽松（schema 放过，由 service PUSH_TASK_TYPE_NOT_MANUAL 拒，保留专用错误码语义）。
 * 新增 task_type 须同步加 variant——push.test.ts「全 task_type 覆盖」用例兜底（漏加即红）。
 */
const pushTaskBase = {
  target_scope: z.enum(['all_apps', 'app']),
  target_app_id: z.string().uuid().nullable().optional(),
  // scheduled_at 是**绝对时刻入参**（非章节定时发布那种「墙上时刻」例外字段，50-命名与类型规范 §2.2.1）。
  // `z.string().datetime()`（zod 4 无 offset:true）只接受 UTC `Z` 结尾形态、拒绝 `+02:00` 数字 offset
  // —— web 端必须序列化为 `new Date(...).toISOString()` 的 UTC Z（F1）。
  scheduled_at: z.string().datetime().nullable().optional(),
};

/**
 * 过去时刻守卫（F2）：scheduled_at 不传 / null（立即发送）放行；带值时必须严格晚于当前时刻。
 * route schema 仅校形状，createTask 直接 `new Date(scheduled_at)` 落库 PENDING → 脚本 / stale client /
 * devtools 可建过去时刻任务。本 refine 在路由 parseBody 层即拒（VALIDATION_FAILED + errors[].field=scheduled_at），
 * service 层另有 defense-in-depth 守卫。ADM-PUSH-S003 未定义 scheduled_at 参照时区，故仅校「绝对时刻 > now」语义。
 */
function scheduledAtNotPast(v: { scheduled_at?: string | null }): boolean {
  return v.scheduled_at == null || new Date(v.scheduled_at).getTime() > Date.now();
}
const SCHEDULED_AT_NOT_PAST_REFINE = {
  message: 'scheduled_at must be in the future',
  path: ['scheduled_at'],
};
const loosePushPayload = z.record(z.string(), z.unknown());

export const CreatePushTaskRequestSchema = z
  .discriminatedUnion('task_type', [
    z.object({
      task_type: z.literal('BOOK_RECOMMEND'),
      payload: PushBookRecommendPayloadSchema,
      ...pushTaskBase,
    }),
    z.object({
      task_type: z.literal('TOPUP_PROMO'),
      payload: PushTopupPromoPayloadSchema,
      ...pushTaskBase,
    }),
    z.object({ task_type: z.literal('SIGNIN_REMIND'), payload: loosePushPayload, ...pushTaskBase }),
    z.object({ task_type: z.literal('USER_RECALL'), payload: loosePushPayload, ...pushTaskBase }),
    z.object({
      task_type: z.literal('CHAPTER_UPDATE'),
      payload: loosePushPayload,
      ...pushTaskBase,
    }),
    z.object({
      task_type: z.literal('BOOK_DELIST_NOTICE'),
      payload: loosePushPayload,
      ...pushTaskBase,
    }),
    z.object({
      task_type: z.literal('SUBSCRIPTION_RENEWAL_REMIND'),
      payload: loosePushPayload,
      ...pushTaskBase,
    }),
    z.object({
      task_type: z.literal('SUBSCRIPTION_RENEWAL_SUCCESS'),
      payload: loosePushPayload,
      ...pushTaskBase,
    }),
    z.object({ task_type: z.literal('SUPPORT_REPLY'), payload: loosePushPayload, ...pushTaskBase }),
  ])
  .refine((v) => v.target_scope === 'all_apps' || !!v.target_app_id, {
    message: 'target_app_id is required when target_scope=app',
    path: ['target_app_id'],
  })
  .refine(scheduledAtNotPast, SCHEDULED_AT_NOT_PAST_REFINE);
export type CreatePushTaskRequest = z.infer<typeof CreatePushTaskRequestSchema>;

export type PushTemplateResponse = {
  id: string;
  template_slug: string;
  category: PushTemplateCategory;
  title_i18n: Record<string, string>;
  body_i18n: Record<string, string>;
  /** server 预置 baseline（禁删/禁改 slug）；功能类（≠OFFER）禁停 */
  is_seed_baseline: boolean;
  is_active: boolean;
  /** 注册表派生值，不是物理列（A4 §1）；`CONFIRMED_MESSAGE` 模板页只读 */
  content_source: PushTemplateContentSource;
};

/** push_tasks.status 闭集（= schema/admin/push-tasks default 'PENDING'，DB varchar(16)）*/
// CANCELLED 入闭集（adversarial r1）：service 写 + DTO 支持 CANCELLED，列表筛选枚举须含它，
// 否则 status=CANCELLED → 422、UI 派生不出「已取消」筛选项。与 PushTaskResponse.status 同一闭集。
export const PUSH_TASK_STATUSES = [
  'PENDING',
  'SENDING',
  'COMPLETED',
  'FAILED',
  'CANCELLED',
] as const;
export type PushTaskStatus = (typeof PUSH_TASK_STATUSES)[number];

/**
 * 推送历史列表查询参数（ADM-PUSH-S005）— 服务端过滤下推（前端不再单页客户端过滤）。
 * cursor/limit 仍走 lib/cursor 的 parsePageParams（复合 keyset：created_at DESC, id DESC）；以下为可选过滤维度。
 * 多值参数（status / app_id / task_type / operator）走 `c.req.queries('<name>')`（重复 query 参数 → string[]），单值走 `c.req.query`。
 * - status：状态闭集多选 → service inArray(push_tasks.status)；未知值 → 422 VALIDATION_FAILED。
 * - target_scope：投放范围单选 'all_apps' | 'app' → service eq(push_tasks.target_scope)。
 * - app_id：按具体 App 多选（uuid）→ service inArray(push_tasks.target_app_id）。
 * - task_type：任务类型闭集多选 → service inArray(push_tasks.task_type)；均 admin-own push_tasks 直接列。
 * - operator：按操作员多选（created_by = admin_users.id 的 uuid）→ service inArray(push_tasks.created_by)。
 *   按 created_by 而非 nickname 下推：created_by 在 push_tasks 直接列（count/data 同 WHERE 无需 join），nickname 非唯一且可改。
 * push_tasks 无 title 列，标题搜索仍不下推（前端按 id/type/scope 当前页过滤，见 PushPage）。
 */
export const ListPushTasksQuerySchema = z.object({
  status: z.array(z.enum(PUSH_TASK_STATUSES)).optional(),
  target_scope: z.enum(['all_apps', 'app']).optional(),
  app_id: z.array(z.string().uuid()).optional(),
  task_type: z.array(PushTaskTypeSchema).optional(),
  operator: z.array(z.string().uuid()).optional(),
});
export type ListPushTasksQuery = z.infer<typeof ListPushTasksQuerySchema>;

export type PushTaskResponse = {
  id: string;
  /** 历史投放任务的业务类型快照（方案 A 保留）*/
  task_type: PushTaskType;
  /** 关联的推送模板 slug（方案 A 新增；= TEMPLATE_SLUG_BY_TASK_TYPE[task_type]）*/
  template_slug: string;
  target_scope: 'all_apps' | 'app';
  target_app_id: string | null;
  payload: Record<string, unknown>;
  // CANCELLED：运营取消待发布任务（POST /push/tasks/:id/cancel）；push_tasks 是 admin 独有表、varchar 状态无 DB 枚举约束
  status: 'PENDING' | 'SENDING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
  scheduled_at: string | null;
  sent_at: string | null;
  total_delivered: number;
  total_failed: number;
  created_at: string;
  /** 创建该任务的运营 admin_users.id（notNull）。供前端「操作员」筛选按 created_by 服务端下推用。 */
  created_by: string;
  /**
   * 操作员显示名 = created_by（admin_users.id）经 left join admin_users.nickname 解析（ADM-PUSH-S005 接真）。
   * created_by 为 notNull，但操作员账号被硬删（罕见，admin 默认软删保留行）时 join 落空 → null。
   */
  operator: string | null;
};
